import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  arrayRemove,
  where,
  writeBatch,
  type DocumentData,
} from 'firebase/firestore'
import { getDbOrThrow } from '@/lib/firebase'
import {
  mapEffect,
  mapEstateType,
  mapGroup,
  mapHint,
  mapOption,
  mapQuestion,
  mapSectionType,
} from '@/lib/rule-mappers'
import type {
  AnswerType,
  ConfigQuestion,
  EstateType,
  MaterialEffect,
  QuestionGroup,
  QuestionHint,
  QuestionOption,
  SectionType,
} from '@/lib/types'

/**
 * The calculation-rules tree: `ruleEstateTypes` → `ruleSectionTypes` → `ruleQuestions` →
 * `ruleQuestionOptions` / `ruleMaterialEffects`, plus the reusable `ruleQuestionGroups` and their
 * `ruleQuestionHints`.
 *
 * All seven collections are admin-only for writes and readable by any signed-in user (the rules file),
 * and all of them are small, so the console subscribes to each WHOLE collection and filters/sorts in
 * memory — exactly like the app's cached config. No `orderBy`, therefore no Firestore index, and a
 * document with a missing `orderIndex` is never dropped from the result.
 *
 * Firestore has no cascade delete, so the deletes below replicate the app's manual cascades.
 */

/** Generic whole-collection subscription with an in-memory mapper and sort. */
function subscribeCollection<T>(
  name: string,
  map: (id: string, data: DocumentData | undefined) => T | null,
  compare: (left: T, right: T) => number,
  onData: (items: T[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    collection(getDbOrThrow(), name),
    (snapshot) => {
      const items = snapshot.docs
        .map((item) => map(item.id, item.data()))
        .filter((item): item is T => item !== null)
        .sort(compare)
      onData(items)
    },
    onError,
  )
}

const byOrder =
  <T extends { orderIndex: number }>(nameOf: (item: T) => string) =>
  (left: T, right: T): number =>
    left.orderIndex !== right.orderIndex
      ? left.orderIndex - right.orderIndex
      : nameOf(left).localeCompare(nameOf(right), 'ru')

export const subscribeToEstateTypes = (
  onData: (items: EstateType[]) => void,
  onError: (error: Error) => void,
) =>
  subscribeCollection(
    'ruleEstateTypes',
    mapEstateType,
    byOrder<EstateType>((item) => item.name),
    onData,
    onError,
  )

export const subscribeToSectionTypes = (
  onData: (items: SectionType[]) => void,
  onError: (error: Error) => void,
) =>
  subscribeCollection(
    'ruleSectionTypes',
    mapSectionType,
    byOrder<SectionType>((item) => item.name),
    onData,
    onError,
  )

export const subscribeToGroups = (
  onData: (items: QuestionGroup[]) => void,
  onError: (error: Error) => void,
) =>
  subscribeCollection(
    'ruleQuestionGroups',
    mapGroup,
    byOrder<QuestionGroup>((item) => item.name),
    onData,
    onError,
  )

export const subscribeToHints = (
  onData: (items: QuestionHint[]) => void,
  onError: (error: Error) => void,
) =>
  subscribeCollection(
    'ruleQuestionHints',
    mapHint,
    byOrder<QuestionHint>((item) => item.text),
    onData,
    onError,
  )

export const subscribeToQuestions = (
  onData: (items: ConfigQuestion[]) => void,
  onError: (error: Error) => void,
) =>
  subscribeCollection(
    'ruleQuestions',
    mapQuestion,
    byOrder<ConfigQuestion>((item) => item.prompt),
    onData,
    onError,
  )

export const subscribeToOptions = (
  onData: (items: QuestionOption[]) => void,
  onError: (error: Error) => void,
) =>
  subscribeCollection(
    'ruleQuestionOptions',
    mapOption,
    byOrder<QuestionOption>((item) => item.label),
    onData,
    onError,
  )

export const subscribeToEffects = (
  onData: (items: MaterialEffect[]) => void,
  onError: (error: Error) => void,
) =>
  subscribeCollection(
    'ruleMaterialEffects',
    mapEffect,
    (left, right) => left.expression.localeCompare(right.expression, 'ru'),
    onData,
    onError,
  )

// ---- Writes ----

function ref(collectionName: string, id: string | null) {
  const db = getDbOrThrow()
  return id === null ? doc(collection(db, collectionName)) : doc(db, collectionName, id)
}

export type EstateTypeDraft = { name: string; nameI18n: Record<string, string>; orderIndex: number }

export function saveEstateType(id: string | null, draft: EstateTypeDraft): Promise<void> {
  return setDoc(ref('ruleEstateTypes', id), {
    name: draft.name,
    nameI18n: draft.nameI18n,
    orderIndex: draft.orderIndex,
  })
}

export type SectionTypeDraft = EstateTypeDraft & { estateTypeId: string; groupIds: string[] }

export function saveSectionType(id: string | null, draft: SectionTypeDraft): Promise<void> {
  return setDoc(ref('ruleSectionTypes', id), {
    estateTypeId: draft.estateTypeId,
    name: draft.name,
    nameI18n: draft.nameI18n,
    orderIndex: draft.orderIndex,
    groupIds: draft.groupIds,
  })
}

/**
 * Unlinks groups from a section type WITHOUT rewriting the rest of the document (`arrayRemove`):
 * the quick fix of "Несоответствия" for groups that no longer exist.
 */
export function removeSectionGroups(sectionTypeId: string, groupIds: string[]): Promise<void> {
  return updateDoc(doc(getDbOrThrow(), 'ruleSectionTypes', sectionTypeId), {
    groupIds: arrayRemove(...groupIds),
  })
}

export type GroupDraft = EstateTypeDraft & { isGlobal: boolean }

export function saveGroup(id: string | null, draft: GroupDraft): Promise<void> {
  return setDoc(ref('ruleQuestionGroups', id), {
    name: draft.name,
    nameI18n: draft.nameI18n,
    orderIndex: draft.orderIndex,
    isGlobal: draft.isGlobal,
  })
}

export type HintDraft = {
  groupId: string
  text: string
  textI18n: Record<string, string>
  condition: string
  orderIndex: number
}

export function saveHint(id: string | null, draft: HintDraft): Promise<void> {
  return setDoc(ref('ruleQuestionHints', id), { ...draft })
}

export function deleteHint(id: string): Promise<void> {
  return deleteDoc(doc(getDbOrThrow(), 'ruleQuestionHints', id))
}

export type QuestionDraft = {
  /** Exactly one of these two is non-blank: a question belongs to a section OR to a group. */
  sectionTypeId: string
  groupId: string
  factKey: string
  prompt: string
  promptI18n: Record<string, string>
  answerType: AnswerType
  unit: string
  /** Copy of the category's current name; the link is `materialCategoryId`. */
  materialCategory: string
  materialCategoryId: string
  orderIndex: number
  displayCondition: string
}

/** Returns the question id, so a freshly created question can immediately get options/effects. */
export function saveQuestion(id: string | null, draft: QuestionDraft): Promise<string> {
  const target = ref('ruleQuestions', id)
  return setDoc(target, { ...draft }).then(() => target.id)
}

export type OptionDraft = {
  questionId: string
  value: string
  label: string
  labelI18n: Record<string, string>
  orderIndex: number
  fieldType: AnswerType
  materialCategory: string
  materialCategoryId: string
}

export type EffectDraft = {
  questionId: string
  materialId: string
  expression: string
  /** See `MaterialEffect.targetFieldKey`; written as "" when there is nothing to choose. */
  targetFieldKey: string
  /** See `MaterialEffect.fixedMaterial`. */
  fixedMaterial: boolean
}

/**
 * Replaces ALL options and effects of a question in one atomic batch — the port of
 * `replaceQuestionChildren`. Stale children are deleted and the new ones written with fresh ids, so a
 * delete and a set never target the same document, and a half-applied edit is impossible.
 */
export async function replaceQuestionChildren(
  questionId: string,
  options: OptionDraft[],
  effects: EffectDraft[],
): Promise<void> {
  const db = getDbOrThrow()
  const [staleOptions, staleEffects] = await Promise.all([
    getDocs(query(collection(db, 'ruleQuestionOptions'), where('questionId', '==', questionId))),
    getDocs(query(collection(db, 'ruleMaterialEffects'), where('questionId', '==', questionId))),
  ])

  const batch = writeBatch(db)
  for (const item of staleOptions.docs) batch.delete(item.ref)
  for (const item of staleEffects.docs) batch.delete(item.ref)
  for (const option of options) {
    batch.set(doc(collection(db, 'ruleQuestionOptions')), { ...option })
  }
  for (const effect of effects) {
    batch.set(doc(collection(db, 'ruleMaterialEffects')), { ...effect })
  }
  await batch.commit()
}

// ---- Cascade deletes (Firestore has none; these mirror the app's manual cascades) ----

async function deleteQuestionsCascade(questionIds: string[]): Promise<void> {
  if (questionIds.length === 0) return
  const db = getDbOrThrow()
  const batch = writeBatch(db)
  for (const questionId of questionIds) {
    const [options, effects] = await Promise.all([
      getDocs(query(collection(db, 'ruleQuestionOptions'), where('questionId', '==', questionId))),
      getDocs(query(collection(db, 'ruleMaterialEffects'), where('questionId', '==', questionId))),
    ])
    for (const item of options.docs) batch.delete(item.ref)
    for (const item of effects.docs) batch.delete(item.ref)
    batch.delete(doc(db, 'ruleQuestions', questionId))
  }
  await batch.commit()
}

/** A question plus its options and effects. */
export function deleteQuestion(id: string): Promise<void> {
  return deleteQuestionsCascade([id])
}

/** A section type plus every question under it (and their children). */
export async function deleteSectionType(id: string): Promise<void> {
  const db = getDbOrThrow()
  const questions = await getDocs(
    query(collection(db, 'ruleQuestions'), where('sectionTypeId', '==', id)),
  )
  await deleteQuestionsCascade(questions.docs.map((item) => item.id))
  await deleteDoc(doc(db, 'ruleSectionTypes', id))
}

/** An estate type plus all of its section types (each cascaded). */
export async function deleteEstateType(id: string): Promise<void> {
  const db = getDbOrThrow()
  const sections = await getDocs(
    query(collection(db, 'ruleSectionTypes'), where('estateTypeId', '==', id)),
  )
  for (const section of sections.docs) await deleteSectionType(section.id)
  await deleteDoc(doc(db, 'ruleEstateTypes', id))
}

/** A group plus its questions (cascaded) and its hints. */
export async function deleteGroup(id: string): Promise<void> {
  const db = getDbOrThrow()
  const [questions, hints] = await Promise.all([
    getDocs(query(collection(db, 'ruleQuestions'), where('groupId', '==', id))),
    getDocs(query(collection(db, 'ruleQuestionHints'), where('groupId', '==', id))),
  ])
  await deleteQuestionsCascade(questions.docs.map((item) => item.id))
  const batch = writeBatch(db)
  for (const hint of hints.docs) batch.delete(hint.ref)
  batch.delete(doc(db, 'ruleQuestionGroups', id))
  await batch.commit()
}
